// react
import { useCallback, useRef, useState } from 'react';

// i18n

// mui
import {
	alpha,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	CheckCircle as IconCheck,
	Close as IconClose,
	CloudUpload as IconUpload,
	Description as IconFile,
	ErrorOutline as IconError,
} from '@mui/icons-material';

// services
import { uploadAiDocument } from '../../../services/aiApi';

// data
import { COLORS } from '../../../data';

// Types

interface DialogUploadDocumentProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	/** Project ID for document isolation — attached to uploaded documents */
	projectId?: number;
	/** Pre-set source type for contextual uploads (e.g. 'contract', 'condition'). Hides the source type dropdown. */
	sourceType?: string;
	/** Pre-set source ID for contextual uploads. Linked automatically on upload. */
	sourceId?: number;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

// Constants

const DOC_TYPE_OPTIONS = [
	{ value: '', label: 'Auto-detect' },
	{ value: 'contract', label: 'Contract' },
	{ value: 'invoice', label: 'Invoice' },
	{ value: 'payment', label: 'Payment' },
	{ value: 'report', label: 'Report' },
	{ value: 'compliance', label: 'Compliance' },
	{ value: 'other', label: 'Other' },
];

const SOURCE_TYPE_OPTIONS = [
	{ value: '', label: 'None' },
	{ value: 'contract', label: 'Contract' },
	{ value: 'activity', label: 'Activity' },
	{ value: 'beneficiary', label: 'Beneficiary' },
	{ value: 'transaction', label: 'Transaction' },
	{ value: 'payment', label: 'Payment' },
	{ value: 'invoice', label: 'Invoice' },
];

const ACCEPTED_FILE_TYPES = [
	'.pdf',
	'.doc',
	'.docx',
	'.xls',
	'.xlsx',
	'.csv',
	'.jpg',
	'.jpeg',
	'.png',
	'.gif',
	'.txt',
	'.json',
].join(',');

// Helpers

const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 B';
	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Component

export const DialogUploadDocument = ({
	isOpen,
	onClose,
	onSuccess,
	projectId,
	sourceType: sourceTypeProp,
	sourceId: sourceIdProp,
}: DialogUploadDocumentProps) => {
	// hooks
	const theme = useTheme();

	// When sourceType/sourceId are provided via props (contextual upload),
	// use them directly and hide the classification dropdowns.
	const isContextual = Boolean(sourceTypeProp);

	// state
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [docType, setDocType] = useState('');
	const [sourceType, setSourceType] = useState(sourceTypeProp || '');
	const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
	const [errorMessage, setErrorMessage] = useState('');
	const [isDragOver, setIsDragOver] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	// Reset all state
	const resetState = useCallback(() => {
		setSelectedFile(null);
		setDocType('');
		setSourceType(sourceTypeProp || '');
		setUploadStatus('idle');
		setErrorMessage('');
		setIsDragOver(false);
	}, [sourceTypeProp]);

	// Handlers
	const handleClose = useCallback(() => {
		if (uploadStatus === 'uploading') return; // prevent closing during upload
		resetState();
		onClose();
	}, [uploadStatus, resetState, onClose]);

	const handleFileSelect = useCallback((file: File) => {
		setSelectedFile(file);
		setUploadStatus('idle');
		setErrorMessage('');
	}, []);

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect]
	);

	const handleDrop = useCallback(
		(event: React.DragEvent) => {
			event.preventDefault();
			setIsDragOver(false);
			const file = event.dataTransfer.files?.[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect]
	);

	const handleDragOver = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleBrowseClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleRemoveFile = useCallback(() => {
		setSelectedFile(null);
		setUploadStatus('idle');
		setErrorMessage('');
		if (fileInputRef.current) fileInputRef.current.value = '';
	}, []);

	const handleUpload = useCallback(async () => {
		if (!selectedFile) return;

		setUploadStatus('uploading');
		setErrorMessage('');

		try {
			const effectiveSourceType = sourceTypeProp || sourceType || undefined;
			await uploadAiDocument({
				file: selectedFile,
				doc_type: docType || undefined,
				source_type: effectiveSourceType,
				source_id: sourceIdProp,
				project_id: projectId,
				tag:
					effectiveSourceType && sourceIdProp
						? `${effectiveSourceType}:${sourceIdProp}`
						: undefined,
			});

			setUploadStatus('success');

			// Auto-close after success
			setTimeout(() => {
				resetState();
				onClose();
				onSuccess?.();
			}, 1500);
		} catch (err) {
			setUploadStatus('error');
			setErrorMessage(err instanceof Error ? err.message : 'Upload failed');
		}
	}, [
		selectedFile,
		docType,
		sourceType,
		projectId,
		sourceIdProp,
		sourceTypeProp,
		resetState,
		onClose,
		onSuccess,
	]);

	// ==========================================================================
	// Render
	// ==========================================================================

	return (
		<Dialog
			open={isOpen}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{ sx: { borderRadius: 3 } }}>
			{/* Header */}
			<Box
				sx={{
					alignItems: 'center',
					borderBottom: `1px solid ${theme.palette.divider}`,
					display: 'flex',
					justifyContent: 'space-between',
					px: 3,
					py: 2,
				}}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Avatar
						sx={{
							bgcolor: alpha(COLORS.primary.main, 0.1),
							height: 44,
							width: 44,
						}}>
						<IconUpload sx={{ color: COLORS.primary.main }} />
					</Avatar>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 600 }}>
							Upload Document
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Upload a file to Azure Blob Storage
						</Typography>
					</Box>
				</Stack>
				<IconButton onClick={handleClose} size="small" disabled={uploadStatus === 'uploading'}>
					<IconClose />
				</IconButton>
			</Box>

			<DialogContent sx={{ px: 3, py: 3 }}>
				<Stack spacing={3}>
					{/* Drop Zone */}
					<Paper
						variant="outlined"
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						sx={{
							alignItems: 'center',
							border: '2px dashed',
							borderColor: isDragOver
								? COLORS.primary.main
								: selectedFile
									? COLORS.success.main
									: theme.palette.divider,
							borderRadius: 2,
							bgcolor: isDragOver
								? alpha(COLORS.primary.main, 0.04)
								: selectedFile
									? alpha(COLORS.success.main, 0.04)
									: 'transparent',
							cursor: 'pointer',
							display: 'flex',
							flexDirection: 'column',
							gap: 1.5,
							justifyContent: 'center',
							p: 4,
							textAlign: 'center',
							transition: 'all 0.2s ease',
						}}
						onClick={!selectedFile ? handleBrowseClick : undefined}>
						<input
							ref={fileInputRef}
							type="file"
							accept={ACCEPTED_FILE_TYPES}
							hidden
							onChange={handleInputChange}
						/>

						{selectedFile ? (
							<>
								<IconFile sx={{ color: COLORS.success.main, fontSize: 48 }} />
								<Box>
									<Typography variant="body1" sx={{ fontWeight: 600 }}>
										{selectedFile.name}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{formatFileSize(selectedFile.size)}
									</Typography>
								</Box>
								<Button
									variant="text"
									size="small"
									color="error"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveFile();
									}}
									sx={{ textTransform: 'none' }}>
									Remove file
								</Button>
							</>
						) : (
							<>
								<IconUpload
									sx={{
										color: isDragOver ? COLORS.primary.main : theme.palette.text.secondary,
										fontSize: 48,
									}}
								/>
								<Box>
									<Typography variant="body1" sx={{ fontWeight: 600 }}>
										Drag & drop a file here
									</Typography>
									<Typography variant="body2" color="text.secondary">
										or{' '}
										<Typography
											component="span"
											variant="body2"
											sx={{ color: COLORS.primary.main, cursor: 'pointer', fontWeight: 600 }}>
											browse
										</Typography>{' '}
										to choose a file
									</Typography>
								</Box>
								<Typography variant="caption" color="text.disabled">
									PDF, DOC, XLSX, CSV, Images — max 100 MB
								</Typography>
							</>
						)}
					</Paper>

					{/* Metadata Fields */}
					{selectedFile && (
						<>
							<FormControl size="small" fullWidth>
								<InputLabel>Document Type</InputLabel>
								<Select
									value={docType}
									label="Document Type"
									onChange={(e) => setDocType(e.target.value)}>
									{DOC_TYPE_OPTIONS.map((opt) => (
										<MenuItem key={opt.value} value={opt.value}>
											{opt.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{/* Only show source type dropdown for non-contextual uploads */}
							{!isContextual && (
								<FormControl size="small" fullWidth>
									<InputLabel>Source Type</InputLabel>
									<Select
										value={sourceType}
										label="Source Type"
										onChange={(e) => setSourceType(e.target.value)}>
										{SOURCE_TYPE_OPTIONS.map((opt) => (
											<MenuItem key={opt.value} value={opt.value}>
												{opt.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</>
					)}

					{/* Upload Status Feedback */}
					{uploadStatus === 'uploading' && (
						<Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
							<CircularProgress size={24} />
							<Typography variant="body2" color="text.secondary">
								Uploading {selectedFile?.name}...
							</Typography>
						</Stack>
					)}

					{uploadStatus === 'success' && (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.5}
							sx={{
								bgcolor: alpha(COLORS.success.main, 0.08),
								borderRadius: 2,
								px: 2,
								py: 1.5,
							}}>
							<IconCheck sx={{ color: COLORS.success.main }} />
							<Typography variant="body2" sx={{ color: COLORS.success.main, fontWeight: 600 }}>
								Document uploaded successfully!
							</Typography>
						</Stack>
					)}

					{uploadStatus === 'error' && (
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.5}
							sx={{
								bgcolor: alpha(theme.palette.error.main, 0.08),
								borderRadius: 2,
								px: 2,
								py: 1.5,
							}}>
							<IconError sx={{ color: theme.palette.error.main }} />
							<Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
								{errorMessage || 'Upload failed. Please try again.'}
							</Typography>
						</Stack>
					)}
				</Stack>
			</DialogContent>

			{/* Actions */}
			<DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 3, py: 2 }}>
				<Button
					onClick={handleClose}
					disabled={uploadStatus === 'uploading'}
					sx={{ borderRadius: 2, textTransform: 'none' }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleUpload}
					disabled={!selectedFile || uploadStatus === 'uploading' || uploadStatus === 'success'}
					startIcon={
						uploadStatus === 'uploading' ? (
							<CircularProgress size={18} color="inherit" />
						) : (
							<IconUpload />
						)
					}
					sx={{
						background:
							uploadStatus !== 'uploading'
								? `linear-gradient(135deg, ${COLORS.primary.main} 0%, ${COLORS.ai.main} 100%)`
								: undefined,
						borderRadius: 2,
						color: '#fff',
						fontSize: '0.9375rem',
						px: 3,
						py: 1,
						textTransform: 'none',
						fontWeight: 600,
						'&.Mui-disabled': {
							color: 'rgba(255,255,255,0.6)',
						},
					}}>
					{uploadStatus === 'uploading' ? 'Uploading...' : 'Upload Document'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
